/**
 * React Query hooks for Issue Spotter
 *
 * Provides caching, automatic revalidation, and optimistic updates
 * for Issue Spotter data. Follows the same pattern as use-query-flashcards.ts.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import type { LegalIssue, IRACAnalysis } from './useIssueSpotter';

// Cache duration constants
// Most Issue Spotter data is immutable after creation, so we can cache aggressively
const ISSUE_SPOTTER_CACHE = {
  // Static data - never changes after analysis completes
  STATIC_STALE: Infinity,  // Never auto-refetch
  STATIC_GC: 1000 * 60 * 60 * 24,  // Keep in memory for 24 hours

  // Session list - only changes when creating new sessions
  LIST_STALE: 1000 * 60 * 30,  // 30 minutes
  LIST_GC: 1000 * 60 * 60 * 2,  // 2 hours

  // Practice state - changes during active practice
  PRACTICE_STALE: 1000 * 60 * 5,  // 5 minutes
  PRACTICE_GC: 1000 * 60 * 60,  // 1 hour

  // User progress - aggregated stats, changes slowly
  PROGRESS_STALE: 1000 * 60 * 10,  // 10 minutes
  PROGRESS_GC: 1000 * 60 * 60,  // 1 hour
};

// Query keys for cache management
export const issueSpotterKeys = {
  all: ['issueSpotter'] as const,
  sessions: (userId: string) => [...issueSpotterKeys.all, 'sessions', userId] as const,
  archivedSessions: (userId: string) => [...issueSpotterKeys.all, 'archived-sessions', userId] as const,
  session: (sessionId: string) => [...issueSpotterKeys.all, 'session', sessionId] as const,
  sessionAnalysis: (sessionId: string) => [...issueSpotterKeys.all, 'session-analysis', sessionId] as const,
  progress: (userId: string) => [...issueSpotterKeys.all, 'progress', userId] as const,
};

// Types
export interface SessionSummary {
  session_id: string;
  title?: string | null;
  mode: string;
  subject_areas: string[];
  difficulty: string;
  issue_count: number;
  status: string;
  created_at: string;
}

export interface UserProgress {
  total_sessions: number;
  total_issues_found: number;
  total_issues_possible: number;
  overall_accuracy: number;
  subject_stats: Record<string, { found: number; total: number }>;
  accuracy_history: Array<{ date: string; accuracy: number }>;
  weak_areas: string[];
  current_streak: number;
  longest_streak: number;
}

/**
 * Hook to fetch user's Issue Spotter progress/stats
 * Cached for 5 minutes (user progress data)
 */
// Default empty progress for immediate UI display
const EMPTY_PROGRESS: UserProgress = {
  total_sessions: 0,
  total_issues_found: 0,
  total_issues_possible: 0,
  overall_accuracy: 0,
  subject_stats: {},
  accuracy_history: [],
  weak_areas: [],
  current_streak: 0,
  longest_streak: 0,
};

export function useIssueSpotterProgress() {
  const { user } = useAuth();

  return useQuery({
    queryKey: issueSpotterKeys.progress(user?.id || ''),
    queryFn: async (): Promise<UserProgress> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const startTime = Date.now();
      logger.info('[useIssueSpotterProgress] Fetching progress from Supabase', { userId: user.id });

      // Query Supabase directly for fast loading (no Cloud Run cold start)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('issue_spotter_progress')
        .select('total_sessions, total_issues_found, total_issues_possible, subject_stats, accuracy_history, weak_areas, current_streak, longest_streak')
        .eq('user_id', user.id)
        .maybeSingle();

      const elapsed = Date.now() - startTime;

      if (error) {
        logger.error('[useIssueSpotterProgress] Supabase error', error);
        throw new Error(`Failed to fetch progress: ${error.message}`);
      }

      // maybeSingle() returns null when no rows exist (no HTTP 406)
      if (!data) {
        logger.info(`[useIssueSpotterProgress] No progress record found (${elapsed}ms)`);
        return EMPTY_PROGRESS;
      }

      // Calculate overall accuracy
      const totalFound = data?.total_issues_found || 0;
      const totalPossible = data?.total_issues_possible || 0;
      const overallAccuracy = totalPossible > 0 ? Math.round((totalFound / totalPossible) * 1000) / 10 : 0;

      // Parse JSON fields (they may be stored as strings or already parsed)
      const parseJsonField = (field: unknown, defaultValue: unknown) => {
        if (!field) return defaultValue;
        if (typeof field === 'string') {
          try {
            return JSON.parse(field);
          } catch {
            return defaultValue;
          }
        }
        return field;
      };

      logger.info(`[useIssueSpotterProgress] Fetched progress (${elapsed}ms)`, { sessions: data?.total_sessions || 0 });

      return {
        total_sessions: data?.total_sessions || 0,
        total_issues_found: totalFound,
        total_issues_possible: totalPossible,
        overall_accuracy: overallAccuracy,
        subject_stats: parseJsonField(data?.subject_stats, {}),
        accuracy_history: parseJsonField(data?.accuracy_history, []),
        weak_areas: parseJsonField(data?.weak_areas, []),
        current_streak: data?.current_streak || 0,
        longest_streak: data?.longest_streak || 0,
      };
    },
    enabled: !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.PROGRESS_STALE,
    gcTime: ISSUE_SPOTTER_CACHE.PROGRESS_GC,
    placeholderData: EMPTY_PROGRESS, // Show empty data immediately while loading
    retry: 2,
    retryDelay: 500,
  });
}

/**
 * Hook to fetch user's Issue Spotter sessions
 * Queries Supabase directly for fast loading (no Cloud Run cold start)
 */
export function useIssueSpotterSessions(limit: number = 20, offset: number = 0) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...issueSpotterKeys.sessions(user?.id || ''), limit, offset],
    queryFn: async (): Promise<{ sessions: SessionSummary[] }> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const startTime = Date.now();
      logger.info('[useIssueSpotterSessions] Fetching sessions from Supabase', { userId: user.id, limit, offset });

      // Query Supabase directly for fast loading (no Cloud Run cold start)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .select('id, mode, subject_area, difficulty, identified_issues, status, created_at, title')
        .eq('user_id', user.id)
        .or('is_archived.is.null,is_archived.eq.false')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        logger.error('[useIssueSpotterSessions] Supabase error', error);
        throw new Error(`Failed to fetch sessions: ${error.message}`);
      }

      // Transform to SessionSummary format
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessions: SessionSummary[] = (data || []).map((row: any) => ({
        session_id: row.id,
        title: row.title || null,
        mode: row.mode || 'full_analysis',
        subject_areas: row.subject_area ? row.subject_area.split(',') : [],
        difficulty: row.difficulty || 'medium',
        issue_count: Array.isArray(row.identified_issues) ? row.identified_issues.length : 0,
        status: row.status || 'unknown',
        created_at: row.created_at,
      }));

      const elapsed = Date.now() - startTime;
      logger.info(`[useIssueSpotterSessions] Fetched sessions (${elapsed}ms)`, { count: sessions.length });
      return { sessions };
    },
    enabled: !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.LIST_STALE,
    gcTime: ISSUE_SPOTTER_CACHE.LIST_GC,
    placeholderData: { sessions: [] }, // Show empty list immediately while loading
    retry: 2,
    retryDelay: 500,
  });
}

/**
 * Hook to fetch user's Issue Spotter sessions with infinite scroll
 * Uses useInfiniteQuery for "Load More" functionality
 */
export function useIssueSpotterSessionsInfinite(pageSize: number = 20) {
  const { user } = useAuth();

  return useInfiniteQuery({
    queryKey: [...issueSpotterKeys.sessions(user?.id || ''), 'infinite'],
    queryFn: async ({ pageParam = 0 }): Promise<{ sessions: SessionSummary[]; nextOffset: number | null }> => {
      if (!user?.id) {
        return { sessions: [], nextOffset: null };
      }

      const startTime = Date.now();
      logger.info('[useIssueSpotterSessionsInfinite] Fetching sessions from Supabase', { userId: user.id, offset: pageParam });

      // Query Supabase directly for fast loading (no Cloud Run cold start)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .select('id, mode, subject_area, difficulty, identified_issues, status, created_at, title')
        .eq('user_id', user.id)
        .or('is_archived.is.null,is_archived.eq.false')
        .order('created_at', { ascending: false })
        .range(pageParam, pageParam + pageSize - 1);

      if (error) {
        logger.error('[useIssueSpotterSessionsInfinite] Supabase error', error);
        throw new Error(`Failed to fetch sessions: ${error.message}`);
      }

      // Transform to SessionSummary format
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessions: SessionSummary[] = (data || []).map((row: any) => ({
        session_id: row.id,
        title: row.title || null,
        mode: row.mode || 'full_analysis',
        subject_areas: row.subject_area ? row.subject_area.split(',') : [],
        difficulty: row.difficulty || 'medium',
        issue_count: Array.isArray(row.identified_issues) ? row.identified_issues.length : 0,
        status: row.status || 'unknown',
        created_at: row.created_at,
      }));

      const elapsed = Date.now() - startTime;
      logger.info(`[useIssueSpotterSessionsInfinite] Fetched sessions (${elapsed}ms)`, { count: sessions.length });

      return {
        sessions,
        nextOffset: data && data.length >= pageSize ? pageParam + pageSize : null,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    enabled: !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.LIST_STALE,
    gcTime: ISSUE_SPOTTER_CACHE.LIST_GC,
    retry: 2,
    retryDelay: 500,
  });
}

/**
 * Hook to prefetch Issue Spotter data
 * Call this on hover/focus to warm the cache
 */
export function usePrefetchIssueSpotter() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const prefetch = async () => {
    if (!user?.id) return;

    // Prefetch progress and recent sessions in parallel (both from Supabase for speed)
    await Promise.all([
      queryClient.prefetchQuery({
        queryKey: issueSpotterKeys.progress(user.id),
        queryFn: async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await (supabase as any)
            .from('issue_spotter_progress')
            .select('total_sessions, total_issues_found, total_issues_possible, subject_stats, accuracy_history, weak_areas, current_streak, longest_streak')
            .eq('user_id', user.id)
            .maybeSingle();

          if (error) throw error;
          return data || EMPTY_PROGRESS;
        },
        staleTime: ISSUE_SPOTTER_CACHE.PROGRESS_STALE,
      }),
      queryClient.prefetchQuery({
        queryKey: [...issueSpotterKeys.sessions(user.id), 5, 0],
        queryFn: async () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data, error } = await (supabase as any)
            .from('issue_spotter_sessions')
            .select('id, mode, subject_area, difficulty, identified_issues, status, created_at, title')
            .eq('user_id', user.id)
            .or('is_archived.is.null,is_archived.eq.false')
            .order('created_at', { ascending: false })
            .range(0, 4);

          if (error) throw error;

          // Transform to SessionSummary format (must match useIssueSpotterSessions transform)
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const sessions: SessionSummary[] = (data || []).map((row: any) => ({
            session_id: row.id,
            title: row.title || null,
            mode: row.mode || 'full_analysis',
            subject_areas: row.subject_area ? row.subject_area.split(',') : [],
            difficulty: row.difficulty || 'medium',
            issue_count: Array.isArray(row.identified_issues) ? row.identified_issues.length : 0,
            status: row.status || 'unknown',
            created_at: row.created_at,
          }));

          return { sessions };
        },
        staleTime: ISSUE_SPOTTER_CACHE.LIST_STALE,
      }),
    ]);

    logger.debug('[usePrefetchIssueSpotter] Prefetched Issue Spotter data from Supabase');
  };

  return prefetch;
}

/**
 * Hook to invalidate Issue Spotter cache
 * Call after creating a new session or completing practice
 */
export function useInvalidateIssueSpotter() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const invalidate = () => {
    if (!user?.id) return;

    queryClient.invalidateQueries({ queryKey: issueSpotterKeys.all });
    logger.debug('[useInvalidateIssueSpotter] Invalidated Issue Spotter cache');
  };

  return invalidate;
}

/**
 * Hook to fetch archived Issue Spotter sessions
 */
export function useArchivedIssueSpotterSessions(limit: number = 20, offset: number = 0) {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...issueSpotterKeys.archivedSessions(user?.id || ''), limit, offset],
    queryFn: async (): Promise<{ sessions: SessionSummary[] }> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .select('id, mode, difficulty, status, created_at, identified_issues')
        .eq('user_id', user.id)
        .eq('is_archived', true)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      // Transform to match SessionSummary interface
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sessions: SessionSummary[] = (data || []).map((row: any) => {
        // Extract unique subject areas from identified issues
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allSubjects = row.identified_issues?.map((i: any) => i.subject_area).filter(Boolean) || [];
        const uniqueSubjects = [...new Set(allSubjects)] as string[];

        return {
          session_id: row.id,
          mode: row.mode,
          subject_areas: uniqueSubjects,
          difficulty: row.difficulty,
          issue_count: row.identified_issues?.length || 0,
          status: row.status,
          created_at: row.created_at,
        };
      });

      return { sessions };
    },
    enabled: !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.LIST_STALE,
    gcTime: ISSUE_SPOTTER_CACHE.LIST_GC,
    placeholderData: { sessions: [] },
  });
}

/**
 * Hook to archive an Issue Spotter session
 */
export function useArchiveIssueSpotterSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .update({ is_archived: true })
        .eq('id', sessionId)
        .eq('user_id', user.id);  // Security: ensure user owns the session

      if (error) throw error;
      return sessionId;
    },
    // Optimistic update - immediately remove from history list
    onMutate: async (sessionId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: issueSpotterKeys.sessions(user?.id || '') });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueriesData({ queryKey: issueSpotterKeys.sessions(user?.id || '') });

      // Optimistically update all session queries to remove the archived session
      queryClient.setQueriesData(
        { queryKey: issueSpotterKeys.sessions(user?.id || '') },
        (old: { sessions: SessionSummary[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            sessions: old.sessions.filter(s => s.session_id !== sessionId)
          };
        }
      );

      return { previousSessions };
    },
    onSuccess: () => {
      // Only invalidate the archived sessions query - NOT the main sessions query
      // The main sessions query was already updated optimistically in onMutate
      // Invalidating it would trigger a refetch from the external API which doesn't filter by is_archived
      queryClient.invalidateQueries({ queryKey: issueSpotterKeys.archivedSessions(user?.id || '') });
      toast({
        title: 'Session Archived',
        description: 'Session moved to archive',
      });
    },
    onError: (error, _sessionId, context) => {
      // Rollback on error
      if (context?.previousSessions) {
        context.previousSessions.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('[useArchiveIssueSpotterSession] Error', error);
      toast({
        title: 'Error',
        description: 'Failed to archive session',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to restore an archived Issue Spotter session
 */
export function useRestoreIssueSpotterSession() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      if (!user?.id) throw new Error('User not authenticated');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .update({ is_archived: false })
        .eq('id', sessionId)
        .eq('user_id', user.id);  // Security: ensure user owns the session

      if (error) throw error;
      return sessionId;
    },
    // Optimistic update - immediately remove from archive list
    onMutate: async (sessionId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: issueSpotterKeys.archivedSessions(user?.id || '') });

      // Snapshot the previous value
      const previousSessions = queryClient.getQueriesData({ queryKey: issueSpotterKeys.archivedSessions(user?.id || '') });

      // Optimistically update all archived session queries to remove the restored session
      queryClient.setQueriesData(
        { queryKey: issueSpotterKeys.archivedSessions(user?.id || '') },
        (old: { sessions: SessionSummary[] } | undefined) => {
          if (!old) return old;
          return {
            ...old,
            sessions: old.sessions.filter(s => s.session_id !== sessionId)
          };
        }
      );

      return { previousSessions };
    },
    onSuccess: () => {
      // Only invalidate the main sessions query - NOT the archived sessions query
      // The archived sessions query was already updated optimistically in onMutate
      queryClient.invalidateQueries({ queryKey: issueSpotterKeys.sessions(user?.id || '') });
      toast({
        title: 'Session Restored',
        description: 'Session restored from archive',
      });
    },
    onError: (error, _sessionId, context) => {
      // Rollback on error
      if (context?.previousSessions) {
        context.previousSessions.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('[useRestoreIssueSpotterSession] Error', error);
      toast({
        title: 'Error',
        description: 'Failed to restore session',
        variant: 'destructive',
      });
    },
  });
}

// Re-export types from useIssueSpotter for consumers
export type { LegalIssue, IRACAnalysis };

export interface CrossLink {
  link_type: 'flashcard' | 'outline' | 'case_brief' | 'blog';
  target_id?: string;
  target_slug?: string;
  title: string;
  relevance_score: number;
  context: string;
}

export interface SessionAnalysis {
  session_id: string;
  subject_areas: string[];
  difficulty: string;
  issues: LegalIssue[];
  analyses: Record<string, IRACAnalysis>;
  cross_links: CrossLink[];
  summary: string;
  instructions?: string | null;  // Professor instructions if provided
}

/**
 * Hook to fetch session analysis data with caching
 * This is the main data for the Analysis tab - issues and IRAC analyses
 *
 * Optimized: Queries Supabase directly instead of Cloud Run API for fast loading.
 */
export function useIssueSpotterAnalysis(sessionId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: issueSpotterKeys.sessionAnalysis(sessionId || ''),
    queryFn: async (): Promise<SessionAnalysis | null> => {
      if (!sessionId || !user?.id) return null;

      const startTime = Date.now();
      logger.info('[useIssueSpotterAnalysis] Fetching analysis', { sessionId });

      // Query Supabase directly for fast loading (no Cloud Run cold start)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: dbSession, error } = await (supabase as any)
        .from('issue_spotter_sessions')
        .select('id, subject_area, difficulty, identified_issues, full_analysis, instructions')
        .eq('id', sessionId)
        .eq('user_id', user.id)  // Security: ensure user owns the session
        .single();

      if (error) {
        logger.error('[useIssueSpotterAnalysis] Failed to fetch session', error);
        throw new Error(`Failed to fetch analysis: ${error.message}`);
      }

      if (!dbSession) {
        throw new Error('Session not found');
      }

      // Parse subject_area - can be string or array
      let subjectAreas: string[] = [];
      if (dbSession.subject_area) {
        if (Array.isArray(dbSession.subject_area)) {
          subjectAreas = dbSession.subject_area;
        } else if (typeof dbSession.subject_area === 'string') {
          // Handle comma-separated string format
          subjectAreas = dbSession.subject_area.split(',').map((s: string) => s.trim());
        }
      }

      // Extract issues and analyses from the database
      const issues = dbSession.identified_issues || [];
      const fullAnalysis = dbSession.full_analysis || {};
      const analyses = fullAnalysis.irac_analyses || {};

      const elapsed = Date.now() - startTime;
      logger.info(`[useIssueSpotterAnalysis] Fetched analysis (${elapsed}ms)`, {
        sessionId,
        issueCount: issues.length
      });

      return {
        session_id: sessionId,
        subject_areas: subjectAreas,
        difficulty: dbSession.difficulty || '',
        issues: issues,
        analyses: analyses,
        cross_links: fullAnalysis.cross_links || [],
        summary: fullAnalysis.summary || '',
        instructions: dbSession.instructions || null
      };
    },
    enabled: !!sessionId && !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.STATIC_STALE,  // Never auto-refetch - analysis data is immutable
    gcTime: ISSUE_SPOTTER_CACHE.STATIC_GC,  // Keep in cache for 24 hours
    retry: 2,
    retryDelay: 1000,
  });
}

// Types for practice state
export interface PracticeState {
  session_id: string;
  total_issues: number;
  found_count: number;
  remaining_count: number;
  found_issues: LegalIssue[];
  is_complete: boolean;
}

// UUID validation helper
const isValidUUID = (id: string | undefined): boolean => {
  if (!id) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Hook to fetch and cache practice state for a session
 * This persists found issues across tab switches
 *
 * Optimized: Queries Supabase directly instead of Cloud Run API for fast loading.
 */
export function useIssueSpotterPracticeState(sessionId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Validate sessionId is a proper UUID (not "undefined" string or invalid)
  const validSessionId = isValidUUID(sessionId) ? sessionId : undefined;

  const query = useQuery({
    queryKey: [...issueSpotterKeys.session(validSessionId || ''), 'practice-state'],
    queryFn: async (): Promise<PracticeState | null> => {
      if (!validSessionId || !user?.id) return null;

      const startTime = Date.now();
      logger.info('[useIssueSpotterPracticeState] Fetching practice state from Supabase', { sessionId: validSessionId });

      // Query session to get all identified issues
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: sessionData, error: sessionError } = await (supabase as any)
        .from('issue_spotter_sessions')
        .select('identified_issues')
        .eq('id', validSessionId)
        .eq('user_id', user.id)
        .single();

      if (sessionError) {
        logger.error('[useIssueSpotterPracticeState] Failed to fetch session', sessionError);
        throw new Error(`Failed to fetch session: ${sessionError.message}`);
      }

      const allIssues: LegalIssue[] = sessionData?.identified_issues || [];

      // Query BOTH sources of "spotted" state to build a unified set:
      // 1. issue_spotter_responses (is_correct = true) — written during practice guesses
      // 2. issue_spotter_marks (spotted = true) — written by both practice AND analysis tabs
      // Using the union ensures the practice page count always matches the analysis page.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [responsesResult, marksResult] = await Promise.all([
        (supabase as any)
          .from('issue_spotter_responses')
          .select('matched_issue_id')
          .eq('session_id', validSessionId)
          .eq('user_id', user.id)
          .eq('is_correct', true),
        (supabase as any)
          .from('issue_spotter_marks')
          .select('issue_id')
          .eq('session_id', validSessionId)
          .eq('user_id', user.id)
          .eq('spotted', true)
      ]);

      if (responsesResult.error) {
        logger.error('[useIssueSpotterPracticeState] Failed to fetch responses', responsesResult.error);
        throw new Error(`Failed to fetch responses: ${responsesResult.error.message}`);
      }

      // Merge both sources into a single set of found issue IDs
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const foundIssueIds = new Set([
        ...(responsesResult.data || []).map((r: any) => r.matched_issue_id).filter(Boolean),
        ...(marksResult.data || []).map((r: any) => r.issue_id).filter(Boolean)
      ]);

      // Filter issues into found and remaining
      const foundIssues = allIssues.filter(issue => foundIssueIds.has(issue.issue_id));
      const remainingCount = allIssues.length - foundIssues.length;

      const elapsed = Date.now() - startTime;
      logger.info(`[useIssueSpotterPracticeState] Loaded practice state (${elapsed}ms)`, {
        sessionId: validSessionId,
        foundCount: foundIssues.length,
        totalIssues: allIssues.length
      });

      return {
        session_id: validSessionId,
        total_issues: allIssues.length,
        found_count: foundIssues.length,
        remaining_count: remainingCount,
        found_issues: foundIssues,
        is_complete: remainingCount === 0
      };
    },
    enabled: !!validSessionId && !!user?.id,
    staleTime: ISSUE_SPOTTER_CACHE.PRACTICE_STALE,  // 5 min - updates during practice via addFoundIssue
    gcTime: ISSUE_SPOTTER_CACHE.PRACTICE_GC,  // 1 hour
    retry: 2,
  });

  // Function to add a newly found issue to the cached state
  const addFoundIssue = (issue: LegalIssue) => {
    queryClient.setQueryData(
      [...issueSpotterKeys.session(validSessionId || ''), 'practice-state'],
      (old: PracticeState | null | undefined) => {
        if (!old) return old;
        return {
          ...old,
          found_count: old.found_count + 1,
          remaining_count: old.remaining_count - 1,
          found_issues: [...old.found_issues, issue],
          is_complete: old.remaining_count - 1 === 0
        };
      }
    );
  };

  // Function to remove an issue from the cached state (when un-spotting from Analysis tab)
  const removeFoundIssue = (issueId: string) => {
    queryClient.setQueryData(
      [...issueSpotterKeys.session(validSessionId || ''), 'practice-state'],
      (old: PracticeState | null | undefined) => {
        if (!old) return old;
        const updatedFoundIssues = old.found_issues.filter(i => i.issue_id !== issueId);
        return {
          ...old,
          found_count: updatedFoundIssues.length,
          remaining_count: old.total_issues - updatedFoundIssues.length,
          found_issues: updatedFoundIssues,
          is_complete: false
        };
      }
    );
  };

  return {
    ...query,
    addFoundIssue,
    removeFoundIssue
  };
}
